import { useEffect, useState } from 'react';
import { fetchVideoDetails } from '@api/fetch-video-details';
import { TNullable } from '@common/types';
import { Button } from '@components/ui/button';
import { Pages } from '@constants/pages';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

function VideoViewer() {
  const { docId, instance } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = useState<TNullable<string>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  function handleBackToChat() {
    navigate(instance ? `/instance/${instance}` : Pages.Chat);
  }

  useEffect(() => {
    if (!docId) {
      setVideoUrl(null);
      setHasError(true);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setHasError(false);
    setVideoUrl(null);

    fetchVideoDetails(docId)
      .then((response) => {
        if (!isMounted) return;
        if (response?.videoUrl) {
          setVideoUrl(response.videoUrl);
          return;
        }
        setHasError(true);
      })
      .catch((error: unknown) => {
        console.error(error);
        if (isMounted) {
          setVideoUrl(null);
          setHasError(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [docId]);

  return (
    <div className="relative aspect-video w-full bg-black">
      <Button
        className="fixed left-3 top-3 z-10 gap-2 bg-white/90 text-black shadow-md hover:bg-white sm:left-4 sm:top-4"
        type="button"
        variant="outline"
        onClick={handleBackToChat}
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common.back')}
      </Button>
      {isLoading && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-sm text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t('video.loading')}</span>
        </div>
      )}
      {hasError && !isLoading && (
        <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-white">
          {t('video.unavailable')}
        </div>
      )}
      {videoUrl && !isLoading && !hasError && (
        <video
          className="block h-full w-full bg-black object-contain"
          src={videoUrl}
          controls
          playsInline
          autoPlay
        />
      )}
    </div>
  );
}

export default VideoViewer;
